import React, { useState, useMemo } from 'react';
import { calculateBlockPosition, parseISOString } from '../../utils/timeUtils';
import { getReservationStyle } from '../../utils/colors';
import { useAuth } from '../../context/AuthContext';

/**
 * 予約ブロックコンポーネント（カレンダー上に表示される予約）
 */
const ReservationBlock = ({ reservation, date, onClick, isSpanning = false, isNewlyCreated = false }) => {
  const { isOwnCompanyReservation, isOwnReservation } = useAuth();
  const [isDragging, setIsDragging] = useState(false);

  const isOwnCompany = isOwnCompanyReservation(reservation.company_id);
  const isEditable = reservation.editable || isOwnReservation(reservation.created_by);

  // start_datetimeとend_datetimeがstring型の場合はDateオブジェクトに変換
  // タイムゾーン変換を避けるためparseISOStringを使用
  const startDate = typeof reservation.start_datetime === 'string'
    ? parseISOString(reservation.start_datetime)
    : reservation.start_datetime;
  const endDate = typeof reservation.end_datetime === 'string'
    ? parseISOString(reservation.end_datetime)
    : reservation.end_datetime;

  // 位置とサイズを計算
  const { top, height } = calculateBlockPosition(startDate, endDate, date);

  // 主催者名またはクリエイター名を取得
  const displayName = reservation.organizer_name || reservation.creator_name || '';

  // カードの高さに基づいて表示行数とユーザー名表示を決定
  const displayConfig = useMemo(() => {
    // 高さに応じた表示設定
    // 30分=40px、1時間=80px として計算
    if (height < 35) {
      // 30分未満: 1行のみ、ユーザー名なし
      return { titleLines: 1, showUser: false };
    } else if (height < 55) {
      // 30-40分程度: 1行、ユーザー名1行
      return { titleLines: 1, showUser: true };
    } else if (height < 75) {
      // 45分-1時間程度: 2行、ユーザー名あり
      return { titleLines: 2, showUser: true };
    } else {
      // 1時間以上: 3行以上、ユーザー名あり
      return { titleLines: Math.min(Math.floor((height - 30) / 18), 5), showUser: true };
    }
  }, [height]);
  // 上下に2pxの間隔を確保（絶対配置のため、marginではなく位置と高さで調整）
  const verticalGap = 2;
  const adjustedTop = top + verticalGap;
  const adjustedHeight = Math.max(height - verticalGap * 2, 0);

  // スタイルを取得（M365モードの場合はorganizer_emailを使用）
  const style = getReservationStyle(reservation.company_id, isOwnCompany, isEditable, reservation.room_type, reservation.organizer_email);

  /**
   * ドラッグ開始時の処理
   */
  const handleDragStart = (e) => {
    if (!isEditable) {
      e.preventDefault();
      return;
    }

    setIsDragging(true);

    // ドラッグデータを設定 (start/endがDateオブジェクトまたはstringの可能性に対応)
    const startISO = reservation.start instanceof Date
      ? reservation.start.toISOString()
      : reservation.start_datetime;
    const endISO = reservation.end instanceof Date
      ? reservation.end.toISOString()
      : reservation.end_datetime;

    e.dataTransfer.setData(
      'reservation',
      JSON.stringify({
        id: reservation.id || reservation.reservation_id,
        title: reservation.title,
        start: startISO,
        end: endISO,
        room_type: reservation.room_type,
        company_id: reservation.company_id,
      })
    );
    e.dataTransfer.effectAllowed = 'move';
  };

  /**
   * ドラッグ終了時の処理
   */
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  /**
   * クリック時の処理
   */
  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(reservation);
    }
  };

  /**
   * キーボード操作の処理
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (onClick) {
        onClick(reservation);
      }
    }
  };

  // スクリーンリーダー用のラベル
  const formatTime = (date) => {
    return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };
  const ariaLabel = `${reservation.title}, ${formatTime(startDate)}から${formatTime(endDate)}, ${displayName}${isEditable ? ', 編集可能' : ''}`;

  // A+B予約の場合は2列分の幅にする
  const blockStyle = {
    position: 'absolute',
    top: `${adjustedTop}px`,
    left: '4px',
    height: `${adjustedHeight}px`,
    ...style,
    borderRadius: '4px',
    padding: '4px 8px',
    overflow: 'hidden',
    boxSizing: 'border-box',
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 1,
  };

  if (isSpanning) {
    // 2列跨ぎ: 親要素の幅の2倍 - 左右のマージン分
    blockStyle.width = 'calc(200% - 8px)';
    blockStyle.right = 'auto';
  } else {
    // 通常: 1列分
    blockStyle.right = '4px';
  }

  return (
    <div
      className={`reservation-block ${isDragging ? 'dragging' : ''} ${isEditable ? 'editable' : ''} ${isSpanning ? 'spanning' : ''} ${isNewlyCreated ? 'highlight-new' : ''}`}
      style={blockStyle}
      draggable={isEditable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
    >
      <div
        className="reservation-title"
        style={{
          fontSize: '12px',
          fontWeight: '500',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: displayConfig.titleLines,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.4',
          wordBreak: 'break-word',
        }}
      >
        {isSpanning && <span style={{marginRight: '4px'}}>[A+B]</span>}
        {reservation.title}
      </div>
      {displayConfig.showUser && displayName && (
        <div
          className="reservation-creator"
          style={{
            fontSize: '10px',
            marginTop: '2px',
            opacity: 0.8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </div>
      )}
    </div>
  );
};

export default ReservationBlock;
